/* eslint-disable */
/*!
   _____ ______ __  __ _    _  _____ __  __ ______ _____  
  / ____|  ____|  \/  | |  | |/ ____|  \/  |  ____|  __ \ 
 | |  __| |__  | \  / | |__| | (___ | \  / | |__  | |  | |
 | | |_ |  __| | |\/| |  __  |\___ \| |\/| |  __| | |  | |
 | |__| | |____| |  | | |  | |____) | |  | | |____| |__| |
  \_____|______|_|  |_|_|  |_|_____/|_|  |_|______|_____/                                                                                                                                                                                                                                                                                                   
=========================================================
* GEMHSMED UI - v2.0.0
=========================================================

* Copyright 2024 GEMHS (https://gemhs.net/)

* Designed and Coded by GEMHS

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software. 

*/

import React, { useMemo } from "react";
import {
    Box,
    Icon,
    SimpleGrid,
    Spinner,
    Text,
    Flex,
    Card,
    useColorModeValue,
} from "@chakra-ui/react";
import MiniStatistics from "components/card/MiniStatistics";
import IconBox from "components/icons/IconBox";
import Tasks from "./components/Tasks";
import VisitsChart from "./components/VisitChart";
import PieChart from "./components/PieChart";
import {
    useGetPatientListQuery,
    useGetUsersQuery,
    useGetAppointmentsQuery,
} from "store";
import { MdPeople, MdPerson, MdCalendarToday } from "react-icons/md";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    PieController,
    ArcElement,
} from "chart.js";

// Register required Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PieController
);

function Home() {
    const { data: patients, isFetching, error } = useGetPatientListQuery({
        page: 1,
        pageSize: 10,
    });

    const patientCount = patients ? patients.count : 0;

    const { data: users, isLoading, error: userErr } = useGetUsersQuery({
        page: 1,
        pageSize: 10,
    });

    const userCount = users ? users.count : 0;

    const {
        data: appoinments,
        isLoading: isAppLoading,
        error: appErr,
    } = useGetAppointmentsQuery({
        page: 1,
        pageSize: 10,
    });

    const appointmentCount = appoinments ? appoinments.count : 0;

    // Check if results exist within patientsData
    const patientsData = patients?.results || [];

    // Initialize the last 7 days array with dates and patient count
    const last7Days = Array.from({ length: 7 }, (_, i) => {
        const date = dayjs().subtract(i, "day").format("YYYY-MM-DD");
        return { date, count: 0 };
    }).reverse();

    // Count new patients per day
    patientsData.forEach((patient) => {
        const regDate = dayjs(patient.reg_date).format("YYYY-MM-DD");
        const dayEntry = last7Days.find((day) => day.date === regDate);
        if (dayEntry && patient.is_new) {
            dayEntry.count += 1;
        }
    });

    // Chart data
    const data = {
        labels: last7Days.map((day) => dayjs(day.date).format("ddd")), // Day of the week
        datasets: [
            {
                label: "New Patients",
                data: last7Days.map((day) => day.count),
                backgroundColor: "rgba(75, 192, 192, 0.6)",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 1,
            },
        ],
    };

    // Chart options
    const options = {
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 10, // Increment by 10
                },
            },
        },
    };

    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");
    const textColor = useColorModeValue("secondaryGray.900", "white");

    return (
        <>
            <Helmet>
                <title>Dashboard | GEMHSMED</title>
            </Helmet>
            <Box pt={{ base: "130px", md: "80px", xl: "80px" }} p={5}>
                <VisitsChart />

                <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" my="20px">
                    {/* Left Column with Patient and User Statistics */}
                    <Flex direction="column" gap="20px">
                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={boxBg}
                                    icon={
                                        <Icon
                                            w="32px"
                                            h="32px"
                                            as={MdPerson}
                                            color={brandColor}
                                        />
                                    }
                                />
                            }
                            name="Patients"
                            value={
                                isFetching ? (
                                    <Spinner size="lg" />
                                ) : error ? (
                                    <Text>Error fetching patients</Text>
                                ) : (
                                    patientCount
                                )
                            }
                        />

                        <MiniStatistics
                            startContent={
                                <IconBox
                                    w="56px"
                                    h="56px"
                                    bg={boxBg}
                                    icon={
                                        <Icon
                                            w="32px"
                                            h="32px"
                                            as={MdPeople}
                                            color={brandColor}
                                        />
                                    }
                                />
                            }
                            name="Users"
                            value={
                                isLoading ? (
                                    <Spinner size="lg" />
                                ) : userErr ? (
                                    <Text>Error fetching users</Text>
                                ) : (
                                    userCount
                                )
                            }
                        />
                    </Flex>

                    {/* Right Column with New Patients Bar Chart */}
                    <Card px="15px">
                        <Flex
                            flexDirection="column"
                            align="center"
                            justify="center"
                            textAlign="center"
                            w="auto"
                        >
                            <Text
                                fontSize="xl"
                                fontWeight="bold"
                                color={textColor}
                                m="20px"
                            >
                                New Patients for week
                            </Text>
                            {isFetching ? (
                                <Spinner size="lg" />
                            ) : error ? (
                                <Text color="red.500">
                                    {error.message || "Error fetching patients"}
                                </Text>
                            ) : (
                                <Box width="100%" height="auto">
                                    {" "}
                                    {/* Ensure the chart is responsive and fits within the card */}
                                    <Bar data={data} options={options} />
                                </Box>
                            )}
                        </Flex>
                    </Card>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" mb="20px">
                    <Box>
                        <SimpleGrid rows="2" gap="20px">
                            <PieChart />
                            <MiniStatistics
                                startContent={
                                    <IconBox
                                        w="56px"
                                        h="56px"
                                        bg={boxBg}
                                        icon={
                                            <Icon
                                                w="32px"
                                                h="32px"
                                                as={MdCalendarToday}
                                                color={brandColor}
                                            />
                                        }
                                    />
                                }
                                name="Appointments"
                                value={
                                    isFetching ? (
                                        <Spinner size="lg" />
                                    ) : error ? (
                                        <Text>Error fetching appointment</Text>
                                    ) : (
                                        appointmentCount
                                    )
                                }
                            />
                        </SimpleGrid>
                    </Box>
                    <Tasks />
                </SimpleGrid>
            </Box>
        </>
    );
}

export default Home;
